import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Receipt, Plus, Search, ShieldCheck, CreditCard, FileText, AlertTriangle,
  CheckCircle2, Paperclip, CalendarDays, Building2, X,
} from "lucide-react";
import { toast } from "sonner";
import { useTrackedSearch } from "../activityTracker";

const POLL_MS = 15000;

const STATUS_COLORS = {
  Pending: "text-slate-300 border-slate-600/60 bg-slate-600/10",
  Verified: "text-blue-300 border-blue-500/40 bg-blue-500/10",
  Paid: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  Overdue: "text-red-300 border-red-500/40 bg-red-500/10",
};

const PAYMENT_METHODS = ["Bank Transfer", "Cash", "Cheque", "Online Banking"];

function rm(n) {
  if (n == null) return "—";
  return `RM ${Number(n).toLocaleString("en-MY", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Bills() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAdd = isAdmin || !!user?.bills_can_add;
  const canVerify = isAdmin || !!user?.bills_can_verify;
  const canPay = isAdmin || !!user?.bills_can_pay;

  const [companies, setCompanies] = useState([]);
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({ by_company: [], urgent: [] });
  const [filter, setFilter] = useState({ company_id: "", status: "", month: "", bill_type: "", q: "" });
  useTrackedSearch("bills", filter.q);
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [payTarget, setPayTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [companiesOpen, setCompaniesOpen] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [c, b, s] = await Promise.all([
        api.get("/bills/companies"),
        api.get("/bills", { params: filter }),
        api.get("/bills/summary"),
      ]);
      setCompanies(c.data);
      setBills(b.data);
      setSummary(s.data);
    } catch (e) {
      // on 403 the route itself gates; don't spam toasts
    }
  }, [filter]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    const t = setInterval(loadAll, POLL_MS);
    return () => clearInterval(t);
  }, [loadAll]);

  const activeCompanies = useMemo(
    () => companies.filter((c) => c.is_active !== false),
    [companies]
  );

  const verify = async (b) => {
    try {
      await api.post(`/bills/${b.id}/verify`);
      toast.success("Bill verified");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const unverify = async (b) => {
    try {
      await api.post(`/bills/${b.id}/unverify`);
      toast.success("Reverted to Pending");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteBill = async (b) => {
    if (!window.confirm(`Delete bill "${b.bill_number || b.bill_type}"?`)) return;
    try {
      await api.delete(`/bills/${b.id}`);
      toast.success("Bill deleted");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAttachment = async (b, kind) => {
    try {
      const res = await api.get(`/bills/${b.id}/attachment/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open attachment"); }
  };

  const totalOutstandingDisplay = summary.total_outstanding || 0;
  const totalPaidDisplay = canPay ? (summary.total_paid || 0) : null;

  return (
    <Layout title="Bills & Payments">
      <div className="space-y-4" data-testid="bills-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Receipt className="w-5 h-5 text-orange-400" /> Bills & Payments
          </h1>
          <div className="flex-1" />
          {isAdmin && (
            <Button variant="outline" onClick={() => setCompaniesOpen(true)}
              className="border-slate-700 hover:border-orange-500" data-testid="manage-companies-btn">
              <Building2 className="w-4 h-4 mr-1.5" /> Companies
            </Button>
          )}
          {canAdd && (
            <Button onClick={() => setAddOpen(true)}
              className="bg-orange-600 hover:bg-orange-500 text-white" data-testid="add-bill-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New bill
            </Button>
          )}
        </div>

        {/* Scorecards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ScoreCard label="Pending" value={summary.pending_count || 0} />
          <ScoreCard label="Overdue" value={summary.overdue_count || 0} tone="red" />
          <ScoreCard label="Outstanding (RM)" value={rm(totalOutstandingDisplay)} />
          {canPay && <ScoreCard label="Paid (RM)" value={rm(totalPaidDisplay)} tone="emerald" />}
        </div>

        {/* Urgent banner */}
        {(summary.urgent || []).length > 0 && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3" data-testid="urgent-banner">
            <div className="flex items-center gap-2 text-sm text-red-300 font-medium mb-1">
              <AlertTriangle className="w-4 h-4" /> {summary.overdue_count} overdue
              {summary.due_soon_count > 0 && <> · {summary.due_soon_count} due in next 7 days</>}
            </div>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {summary.urgent.slice(0, 12).map((u) => (
                <button key={u.id} onClick={() => setDetailId(u.id)}
                  className="text-[11px] num px-2 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-200 hover:bg-red-500/20"
                  data-testid={`urgent-${u.id}`}>
                  {u.company_name} · {u.bill_type} · {u.due_date} · {rm(u.amount)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input placeholder="Search number, account, company, type…"
              value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 pl-8 h-9 w-64" data-testid="bills-search" />
          </div>
          <Select value={filter.company_id || "all"}
            onValueChange={(v) => setFilter({ ...filter, company_id: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-56" data-testid="bills-filter-company">
              <SelectValue placeholder="All companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {activeCompanies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}{c.group === "own" ? " · OWN" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filter.status || "all"}
            onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-40" data-testid="bills-filter-status">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Verified">Verified</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Input type="month" value={filter.month}
            onChange={(e) => setFilter({ ...filter, month: e.target.value })}
            className="bg-[#0a0e1a] border-slate-800 h-9 num w-40"
            data-testid="bills-filter-month" />
          <Input placeholder="Bill type…" value={filter.bill_type}
            onChange={(e) => setFilter({ ...filter, bill_type: e.target.value })}
            className="bg-[#0a0e1a] border-slate-800 h-9 w-40" />
          {(filter.q || filter.company_id || filter.status || filter.month || filter.bill_type) && (
            <Button variant="ghost" size="sm" onClick={() => setFilter({ company_id: "", status: "", month: "", bill_type: "", q: "" })}
              className="text-slate-400 hover:text-slate-200">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        {/* Main grid: bills table + by-company outstanding */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
          {/* Balance sheet */}
          <div className="rounded-lg border hairline surface-1 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="bills-table">
                <thead>
                  <tr className="text-left text-xs text-slate-400 uppercase tracking-wider border-b hairline">
                    <th className="py-2.5 px-3">Bill #</th>
                    <th className="px-3">Company</th>
                    <th className="px-3">Type</th>
                    <th className="px-3">Period</th>
                    <th className="px-3">Due</th>
                    <th className="px-3 text-right num">Amount</th>
                    <th className="px-3">Status</th>
                    {canPay && <th className="px-3">Paid</th>}
                    <th className="px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.length === 0 && (
                    <tr><td colSpan={canPay ? 9 : 8} className="text-center text-xs text-slate-500 py-12">
                      No bills match the current filters.
                    </td></tr>
                  )}
                  {bills.map((b) => (
                    <tr key={b.id} className="border-b border-slate-900/80 hover:bg-white/[0.02] cursor-pointer"
                      onClick={() => setDetailId(b.id)}
                      data-testid={`bill-row-${b.id}`}>
                      <td className="py-2 px-3 num text-slate-300">{b.bill_number || <span className="text-slate-600">—</span>}</td>
                      <td className="px-3">
                        <div className="truncate max-w-[180px]">{b.company_name}</div>
                        {b.company_group === "own" && (
                          <span className="text-[9px] uppercase num text-emerald-400 border border-emerald-500/30 rounded px-1">OWN</span>
                        )}
                      </td>
                      <td className="px-3 text-slate-300">{b.bill_type}</td>
                      <td className="px-3 num text-slate-400">{b.billing_period || "—"}</td>
                      <td className="px-3 num text-slate-300">{b.due_date}</td>
                      <td className="px-3 text-right num">{rm(b.amount)}</td>
                      <td className="px-3">
                        <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${STATUS_COLORS[b.status_effective]}`}>
                          {b.status_effective}
                        </span>
                      </td>
                      {canPay && (
                        <td className="px-3 num text-slate-300">
                          {b.payment_date || <span className="text-slate-600">—</span>}
                        </td>
                      )}
                      <td className="px-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end flex-wrap">
                          {b.bill_doc?.filename && (
                            <Button size="sm" variant="ghost" onClick={() => openAttachment(b, "bill_doc")}
                              className="h-7 px-2 text-slate-400" title="View bill document">
                              <Paperclip className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canVerify && b.status === "Pending" && (
                            <Button size="sm" variant="ghost" onClick={() => verify(b)}
                              className="h-7 px-2 text-blue-300" data-testid={`verify-${b.id}`}>
                              <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Verify
                            </Button>
                          )}
                          {canVerify && b.status === "Verified" && (
                            <Button size="sm" variant="ghost" onClick={() => unverify(b)}
                              className="h-7 px-2 text-slate-400">Un-verify</Button>
                          )}
                          {canPay && b.status !== "Paid" && (
                            <Button size="sm" variant="ghost" onClick={() => setPayTarget(b)}
                              className="h-7 px-2 text-emerald-300" data-testid={`pay-${b.id}`}>
                              <CreditCard className="w-3.5 h-3.5 mr-1" /> Pay
                            </Button>
                          )}
                          {canAdd && b.status !== "Paid" && (
                            <Button size="sm" variant="ghost" onClick={() => setEditTarget(b)}
                              className="h-7 px-2 text-slate-400">Edit</Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="ghost" onClick={() => deleteBill(b)}
                              className="h-7 px-2 text-red-400">
                              <X className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Outstanding by company */}
          <div className="rounded-lg border hairline surface-1 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 num mb-2">Outstanding by company</div>
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {(summary.by_company || []).filter((c) => c.outstanding > 0 || c.overdue > 0).length === 0 && (
                <div className="text-xs text-slate-500 text-center py-8">All settled.</div>
              )}
              {(summary.by_company || []).filter((c) => c.outstanding > 0 || c.overdue > 0).map((c) => (
                <button key={c.company_id} onClick={() => setFilter({ ...filter, company_id: c.company_id })}
                  className="w-full text-left px-2 py-1.5 rounded border hairline surface-2 hover:border-orange-500/40 flex items-center justify-between gap-2"
                  data-testid={`outstanding-${c.company_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{c.company_name}
                      {c.company_group === "own" && <span className="text-[9px] text-emerald-400 num ml-1">OWN</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 num">
                      {c.overdue > 0 && <span className="text-red-300">{c.overdue} overdue</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="num text-sm text-orange-300">{rm(c.outstanding)}</div>
                    {canPay && c.paid > 0 && (
                      <div className="text-[10px] text-emerald-300/70 num">Paid {rm(c.paid)}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <BillAddEditDialog
        open={addOpen || !!editTarget}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditTarget(null); } }}
        target={editTarget}
        companies={activeCompanies}
        onDone={() => { setAddOpen(false); setEditTarget(null); loadAll(); }}
      />
      <BillPayDialog open={!!payTarget} onOpenChange={() => setPayTarget(null)}
        bill={payTarget} onDone={() => { setPayTarget(null); loadAll(); }} />
      <BillDetailDialog open={!!detailId} onOpenChange={() => setDetailId(null)}
        billId={detailId} canPay={canPay} onOpenAttachment={openAttachment} />
      {isAdmin && (
        <BillCompaniesDialog open={companiesOpen} onOpenChange={() => setCompaniesOpen(false)}
          companies={companies} onChanged={loadAll} />
      )}
    </Layout>
  );
}

function ScoreCard({ label, value, tone }) {
  const toneCls = tone === "red" ? "text-red-300"
    : tone === "emerald" ? "text-emerald-300" : "text-slate-100";
  return (
    <div className="rounded-lg border hairline surface-1 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className={`text-2xl font-semibold num mt-1 tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

// ---------------- Dialogs ----------------

function BillAddEditDialog({ open, onOpenChange, target, companies, onDone }) {
  const empty = {
    company_id: "", bill_type: "", bill_number: "", account_number: "",
    billing_period: "", due_date: "", amount: "", description: "",
  };
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setForm({
        company_id: target.company_id, bill_type: target.bill_type,
        bill_number: target.bill_number || "",
        account_number: target.account_number || "",
        billing_period: target.billing_period || "",
        due_date: target.due_date || "",
        amount: String(target.amount ?? ""),
        description: target.description || "",
      });
    } else {
      setForm(empty);
    }
    setFile(null);
  }, [open, target]); // eslint-disable-line

  const submit = async () => {
    if (!form.company_id || !form.bill_type || !form.due_date || !form.amount) {
      toast.error("Company, bill type, due date and amount are required");
      return;
    }
    const payload = {
      company_id: form.company_id,
      bill_type: form.bill_type.trim(),
      bill_number: form.bill_number.trim(),
      account_number: form.account_number.trim(),
      billing_period: form.billing_period,
      due_date: form.due_date,
      amount: Number(form.amount),
      description: form.description.trim(),
    };
    if (file) {
      payload.bill_doc = {
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        data_base64: await fileToBase64(file),
      };
    }
    try {
      if (target) {
        await api.put(`/bills/${target.id}`, payload);
        toast.success("Bill updated");
      } else {
        await api.post("/bills", payload);
        toast.success("Bill added");
      }
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg"
        data-testid="bill-add-edit-dialog">
        <DialogHeader>
          <DialogTitle>{target ? "Edit bill" : "New bill"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs text-slate-400">Company</Label>
            <Select value={form.company_id}
              onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="bill-company">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItemsGrouped companies={companies} />
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Bill type</Label>
              <Input value={form.bill_type} onChange={(e) => setForm({ ...form, bill_type: e.target.value })}
                placeholder="TNB, Hostel, Salary…"
                className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="bill-type" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Bill number</Label>
              <Input value={form.bill_number} onChange={(e) => setForm({ ...form, bill_number: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Account number <span className="text-slate-600">(utility / subscriber no.)</span></Label>
            <Input value={form.account_number}
              onChange={(e) => setForm({ ...form, account_number: e.target.value })}
              placeholder="e.g. TNB 22000012345"
              className="bg-[#0a0e1a] border-slate-800 mt-1 num"
              data-testid="bill-account-number" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Billing period</Label>
              <Input type="month" value={form.billing_period}
                onChange={(e) => setForm({ ...form, billing_period: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due date</Label>
              <Input type="date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Amount (RM)</Label>
            <Input type="number" step="0.01" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="bill-amount" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Description / notes</Label>
            <Textarea value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="bg-[#0a0e1a] border-slate-800 mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">
              Bill document (PDF or image){target?.bill_doc ? ` · replaces current: ${target.bill_doc.filename}` : ""}
            </Label>
            <Input type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="bg-[#0a0e1a] border-slate-800 mt-1 file:text-slate-300" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-orange-600 hover:bg-orange-500"
            data-testid="bill-submit">{target ? "Save changes" : "Save bill"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SelectItemsGrouped({ companies }) {
  const own = companies.filter((c) => c.group === "own");
  const client = companies.filter((c) => c.group !== "own");
  return (
    <>
      {own.length > 0 && (
        <>
          <div className="text-[10px] px-2 py-1 uppercase text-slate-500 num">Own companies</div>
          {own.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
          <div className="text-[10px] px-2 py-1 uppercase text-slate-500 num border-t hairline mt-1">Client factories</div>
        </>
      )}
      {client.map((c) => (
        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
      ))}
    </>
  );
}

function BillPayDialog({ open, onOpenChange, bill, onDone }) {
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "Bank Transfer", payment_reference: "",
    payment_amount: "",
  });
  const [file, setFile] = useState(null);

  useEffect(() => {
    if (!open || !bill) return;
    setForm({
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: "Bank Transfer", payment_reference: "",
      payment_amount: String(bill.amount || ""),
    });
    setFile(null);
  }, [open, bill]);

  const submit = async () => {
    if (!form.payment_date || !form.payment_method || !form.payment_amount) {
      toast.error("Date, method and amount are required"); return;
    }
    // Proof of payment is now MANDATORY — staff need it to confirm payments
    // were actually made (audit + dispute resolution).
    if (!file) {
      toast.error("Please attach the payment receipt (PDF or image)");
      return;
    }
    const payload = {
      ...form,
      payment_amount: Number(form.payment_amount),
    };
    payload.payment_receipt = {
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      data_base64: await fileToBase64(file),
    };
    try {
      await api.post(`/bills/${bill.id}/pay`, payload);
      toast.success("Payment recorded");
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!bill) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md" data-testid="bill-pay-dialog">
        <DialogHeader>
          <DialogTitle>Record payment</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-1">
          {bill.company_name} · {bill.bill_type} · {rm(bill.amount)}{bill.bill_number ? ` · ${bill.bill_number}` : ""}
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Payment date</Label>
              <Input type="date" value={form.payment_date}
                onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Method</Label>
              <Select value={form.payment_method}
                onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Reference number</Label>
            <Input value={form.payment_reference}
              onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Amount paid (RM)</Label>
            <Input type="number" step="0.01" value={form.payment_amount}
              onChange={(e) => setForm({ ...form, payment_amount: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 mt-1 num" data-testid="pay-amount" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 flex items-center gap-1.5">
              Payment receipt (PDF or image)
              <span className="text-red-400">*</span>
            </Label>
            <Input type="file" accept="application/pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className={`bg-[#0a0e1a] mt-1 file:text-slate-300 ${file ? "border-emerald-700" : "border-amber-700/60"}`}
              data-testid="pay-receipt-file" />
            <div className={`text-[10px] mt-1 ${file ? "text-emerald-400" : "text-amber-300/80"}`}>
              {file ? `✓ ${file.name}` : "Required — proof of payment for our records"}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-500"
            data-testid="pay-submit">
            <CheckCircle2 className="w-4 h-4 mr-1" /> Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BillDetailDialog({ open, onOpenChange, billId, canPay, onOpenAttachment }) {
  const [bill, setBill] = useState(null);
  useEffect(() => {
    if (!open || !billId) { setBill(null); return; }
    api.get(`/bills/${billId}`).then(({ data }) => setBill(data)).catch(() => {});
  }, [open, billId]);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md" data-testid="bill-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-orange-400" /> Bill detail
          </DialogTitle>
        </DialogHeader>
        {!bill && <div className="text-sm text-slate-500">Loading…</div>}
        {bill && (
          <div className="space-y-2 text-sm">
            <Row k="Company" v={`${bill.company_name}${bill.company_group === "own" ? " · OWN" : ""}`} />
            <Row k="Bill type" v={bill.bill_type} />
            <Row k="Bill number" v={bill.bill_number || "—"} />
            <Row k="Account no." v={bill.account_number || "—"} />
            <Row k="Billing period" v={bill.billing_period || "—"} />
            <Row k="Due date" v={bill.due_date} />
            <Row k="Amount" v={rm(bill.amount)} />
            <Row k="Status" v={
              <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${STATUS_COLORS[bill.status_effective]}`}>
                {bill.status_effective}
              </span>
            } />
            {bill.description && <Row k="Notes" v={bill.description} />}
            <Row k="Added by" v={`${bill.created_by_name || "—"} · ${(bill.created_at || "").slice(0, 10)}`} />
            {bill.verified_by_name && <Row k="Verified by" v={`${bill.verified_by_name} · ${(bill.verified_at || "").slice(0, 10)}`} />}
            {canPay && bill.status === "Paid" && (
              <div className="mt-3 pt-2 border-t hairline space-y-1">
                <div className="text-[10px] uppercase text-emerald-400 num">Payment</div>
                <Row k="Paid on" v={bill.payment_date} />
                <Row k="Method" v={bill.payment_method} />
                <Row k="Reference" v={bill.payment_reference || "—"} />
                <Row k="Amount paid" v={rm(bill.payment_amount)} />
                <Row k="Recorded by" v={`${bill.paid_by_name || "—"}`} />
              </div>
            )}
            <div className="flex items-center gap-2 mt-2">
              {bill.bill_doc?.filename && (
                <Button size="sm" variant="outline" onClick={() => onOpenAttachment(bill, "bill_doc")}
                  className="border-slate-700" data-testid="open-bill-doc">
                  <Paperclip className="w-3.5 h-3.5 mr-1" /> View bill
                </Button>
              )}
              {canPay && bill.payment_receipt?.filename && (
                <Button size="sm" variant="outline" onClick={() => onOpenAttachment(bill, "payment_receipt")}
                  className="border-slate-700" data-testid="open-receipt">
                  <Paperclip className="w-3.5 h-3.5 mr-1" /> View receipt
                </Button>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="text-xs text-slate-500 pt-0.5">{k}</div>
      <div className="text-right text-slate-200 break-words min-w-0">{v}</div>
    </div>
  );
}

function BillCompaniesDialog({ open, onOpenChange, companies, onChanged }) {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [group, setGroup] = useState("client");
  useEffect(() => { setRows(companies || []); }, [companies]);

  const addCo = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/bills/companies", { name: name.trim(), group });
      toast.success("Company added");
      setName("");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (c) => {
    try {
      await api.put(`/bills/companies/${c.id}`, { is_active: !c.is_active });
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-md" data-testid="bill-companies-dialog">
        <DialogHeader>
          <DialogTitle>Bill companies</DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500">Add new companies or deactivate existing ones. Deactivated companies are hidden from new bill forms but keep their historical bills.</div>
        <div className="max-h-[50vh] overflow-y-auto space-y-1 mt-2">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center justify-between px-2 py-1.5 rounded border hairline surface-2">
              <div className="text-sm">
                {c.name}
                {c.group === "own" && <span className="text-[9px] uppercase num text-emerald-400 border border-emerald-500/30 rounded px-1 ml-2">OWN</span>}
              </div>
              <Button size="sm" variant="ghost"
                onClick={() => toggleActive(c)}
                className={`h-7 text-xs ${c.is_active !== false ? "text-slate-300" : "text-red-400"}`}>
                {c.is_active !== false ? "Active" : "Inactive"}
              </Button>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t hairline space-y-2">
          <Label className="text-xs text-slate-400">Add new company</Label>
          <div className="flex gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Company name"
              className="bg-[#0a0e1a] border-slate-800 h-9 flex-1" />
            <Select value={group} onValueChange={setGroup}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="client">Client</SelectItem>
                <SelectItem value="own">Own</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={addCo} className="bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
